import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaTh, 
  FaList, 
  FaDesktop,
  FaExpand,
  FaCogs,
  FaEye,
  FaSignal,
  FaTv,
  FaChevronDown,
  FaInfoCircle,
  FaLayerGroup,
  FaRocket,
  FaClock,
  FaUserLock
} from 'react-icons/fa';
import useScrollAnimation from '../../hooks/useScrollAnimation';
// Import du hook de langue
import { useLanguage } from '../../contexts/LanguageContext'; 

const MursEcrans = () => {
  // Initialisation du context de langue
  const { t } = useLanguage();
  const ds = t.digitalSignage; // Alias pour simplifier l'accès

  // Liste des avantages traduite dynamiquement
  const MURS_ECRANS_ADVANTAGES = [
    {
      id: 'professional-quality',
      name: ds.advantages.quality,
      description: ds.advantages.qualityD,
      icon: <FaUserLock />
    },
    {
      id: 'large-format',
      name: ds.advantages.format,
      description: ds.advantages.formatD,
      icon: <FaExpand />
    },
    {
      id: 'reliable-operation',
      name: ds.advantages.reliable,
      description: ds.advantages.reliableD,
      icon: <FaClock />
    },
    {
      id: 'advanced-features',
      name: ds.advantages.features,
      description: ds.advantages.featuresD,
      icon: <FaRocket />
    }
  ];

  // Données des catégories et produits utilisant les clés du context
  const MURS_ECRANS_DATA = {
    categories: [
      {
        id: 'essential',
        name: ds.categories.essential.name,
        description: ds.categories.essential.desc,
        icon: FaDesktop,
        color: '#3b82f6'
      },
      {
        id: 'midrange',
        name: ds.categories.midrange.name,
        description: ds.categories.midrange.desc,
        icon: FaTv,
        color: '#10b981'
      },
      {
        id: 'professional',
        name: ds.categories.professional.name,
        description: ds.categories.professional.desc,
        icon: FaSignal,
        color: '#f59e0b'
      }
    ],
    
    ecranProducts: [
      {
        id: 1,
        name: "Sharp MultiSync® E869",
        image: "/images/MursEcrans/sharp-e869.jpg",
        category: "essential",
        description: ds.products.e869.desc,
        keyUse: ds.products.e869.use,
        options: ds.products.e869.tech
      },
      {
        id: 2,
        name: "Sharp MultiSync® E759",
        image: "/images/MursEcrans/sharp-e869.jpg",
        category: "essential",
        description: ds.products.e759.desc,
        keyUse: ds.products.e759.use,
        options: ds.products.e759.tech
      },
      {
        id: 3,
        name: "Sharp MultiSync® E659",
        image: "/images/MursEcrans/sharp-e869.jpg",
        category: "essential",
        description: ds.products.e659.desc,
        keyUse: ds.products.e659.use,
        options: ds.products.e659.tech
      },
      {
        id: 4,
        name: "Sharp MultiSync® M552",
        image: "/images/MursEcrans/sharp-m552.jpg",
        category: "midrange",
        description: ds.products.m552.desc,
        keyUse: ds.products.m552.use,
        options: ds.products.m552.tech
      },
      {
        id: 5,
        name: "Sharp MultiSync® ME752",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "midrange",
        description: ds.products.me752.desc,
        keyUse: ds.products.me752.use,
        options: ds.products.me752.tech
      },
      {
        id: 6,
        name: "Sharp MultiSync® ME982",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "midrange",
        description: ds.products.me982.desc,
        keyUse: ds.products.me982.use,
        options: ds.products.me982.tech
      },
      {
        id: 7,
        name: "Sharp MultiSync® ME862",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "midrange",
        description: ds.products.me862.desc,
        keyUse: ds.products.me862.use,
        options: ds.products.me862.tech
      },
      {
        id: 8,
        name: "Sharp MultiSync® P556",
        image: "/images/MursEcrans/sharp-p556.jpg",
        category: "professional",
        description: ds.products.p556.desc,
        keyUse: ds.products.p556.use,
        options: ds.products.p556.tech
      },
      {
        id: 9,
        name: "Sharp MultiSync® P656",
        image: "/images/MursEcrans/sharp-p556.jpg",
        category: "professional",
        description: ds.products.p656.desc,
        keyUse: ds.products.p656.use,
        options: ds.products.p656.tech
      },
      {
        id: 10,
        name: "NEC MultiSync® E328-2",
        image: "/images/MursEcrans/nec-e328.jpg",
        category: "essential",
        description: ds.products.e328.desc,
        keyUse: ds.products.e328.use,
        options: ds.products.e328.tech
      },
      {
        id: 11,
        name: "NEC MultiSync® M651-2",
        image: "/images/MursEcrans/nec-m651.jpg",
        category: "midrange",
        description: ds.products.m651.desc,
        keyUse: ds.products.m651.use,
        options: ds.products.m651.tech
      },
      {
        id: 12,
        name: "NEC MultiSync® M551-2",
        image: "/images/MursEcrans/nec-m651.jpg",
        category: "midrange",
        description: ds.products.m551.desc,
        keyUse: ds.products.m551.use,
        options: ds.products.m551.tech
      }
    ]
  };

  const [filteredProducts, setFilteredProducts] = useState(MURS_ECRANS_DATA.ecranProducts);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 6;
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    let filtered = [...MURS_ECRANS_DATA.ecranProducts];

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.keyUse.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    filtered.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'category') return a.category.localeCompare(b.category);
      return 0;
    });

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy, t]); // Ajout de t pour recalculer si la langue change

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const getCategoryInfo = (categoryId) => {
    return MURS_ECRANS_DATA.categories.find(cat => cat.id === categoryId);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Section Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
        padding: '6rem 0 4rem 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="text-center text-lg-start">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Badge 
                  bg="danger" 
                  className="mb-3"
                  style={{
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  <FaDesktop className="me-2" />
                  {ds.hero.badge}
                </Badge>
                
                <h1 style={{
                  fontSize: '3.5rem',
                  fontWeight: 700,
                  color: '#fff',
                  marginBottom: '1.5rem',
                  lineHeight: '1.2'
                }} dangerouslySetInnerHTML={{ __html: ds.hero.title }}>
                </h1>
                
                <p style={{
                  fontSize: '1.3rem',
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '2rem',
                  lineHeight: '1.6',
                  maxWidth: '600px'
                }}>
                  {ds.hero.subtitle}
                </p>
                
                <div className="d-flex flex-wrap gap-3">
                  <Button 
                    variant="danger" 
                    size="lg"
                    onClick={() => document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(217,0,0,0.4)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaEye className="me-2" />
                    {ds.hero.btnExplore}
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="lg"
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px'
                    }}
                  >
                    <FaInfoCircle className="me-2" />
                    {ds.hero.btnGuide}
                  </Button>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '20px',
                  padding: '2rem',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <img 
                  src="/images/MursEcrans/PublicDisplaysImage.jpg" 
                  alt="Public Display"
                  style={{
                    width: '300px',
                    height: '180px',
                    objectFit: 'cover',
                    marginBottom: '1rem',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                  }}
                />
                <h4 style={{ color: '#fff', marginBottom: '1rem' }}>
                  {ds.hero.cardTitle}
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                  {ds.hero.cardSub}
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section principale avec filtres et produits */}
      <section id="solutions-section" className="py-5" style={{ backgroundColor: '#f8fafc' }}>
        <Container>
          <Row>
            {/* Sidebar avec filtres */}
            <Col lg={3}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: '2rem',
                marginBottom: '2rem'
              }}>
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    <FaSearch className="me-2" />
                    {ds.sidebar.search}
                  </label>
                  <Form.Control
                    type="text"
                    placeholder={ds.sidebar.searchPl}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      fontSize: '0.9rem',
                      padding: '0.75rem 1rem'
                    }}
                  />
                </div>

                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    <FaFilter className="me-2" />
                    {ds.sidebar.category}
                  </label>
                  
                  <div className="d-grid gap-2">
                    <Button
                      variant={selectedCategory === 'all' ? 'primary' : 'outline-secondary'}
                      onClick={() => handleCategoryChange('all')}
                      size="sm"
                      style={{ borderRadius: '10px', fontSize: '0.8rem', padding: '0.5rem 0.75rem', textAlign: 'left' }}
                    >
                      {ds.sidebar.all}
                    </Button>
                    {MURS_ECRANS_DATA.categories.map(category => (
                      <Button
                        key={category.id}
                        variant={selectedCategory === category.id ? 'primary' : 'outline-secondary'}
                        onClick={() => handleCategoryChange(category.id)}
                        size="sm"
                        style={{ borderRadius: '10px', fontSize: '0.8rem', padding: '0.5rem 0.75rem', textAlign: 'left' }}
                      >
                        <category.icon className="me-2" /> {category.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    {t.storage.sidebar.viewLabel}
                  </label>
                  <div className="d-flex gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                      onClick={() => setViewMode('grid')}
                      size="sm"
                      style={{ borderRadius: '10px', flex: 1 }}
                    >
                      <FaTh />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                      onClick={() => setViewMode('list')}
                      size="sm"
                      style={{ borderRadius: '10px', flex: 1 }}
                    >
                      <FaList />
                    </Button>
                  </div>
                </div>

                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                  borderRadius: '12px',
                  border: '1px solid #dee2e6',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ color: '#495057', margin: 0, fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>
                    <strong style={{ color: '#3b82f6' }}>{filteredProducts.length}</strong> {ds.sidebar.found}
                  </p>
                  {totalPages > 1 && (
                    <p style={{ color: '#6c757d', margin: '0.25rem 0 0 0', fontSize: '0.75rem', textAlign: 'center' }}>
                      Page {currentPage} {ds.sidebar.pageOf} {totalPages}
                    </p>
                  )}
                </div>
              </div>
            </Col>

            {/* Zone principale des produits */}
            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedProducts.map((product, index) => (
                    <Col md={6} xl={4} key={product.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        whileHover={{ y: -5 }}
                        style={{ height: '100%' }}
                      >
                        <Card style={{
                          height: '100%',
                          borderRadius: '20px',
                          border: 'none',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{
                            height: '200px',
                            background: `linear-gradient(135deg, ${getCategoryInfo(product.category)?.color}20, ${getCategoryInfo(product.category)?.color}40)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}>
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', flexDirection: 'column' }}>
                              <FaTv size={60} color={getCategoryInfo(product.category)?.color} />
                              <p style={{ color: '#64748b', marginTop: '1rem', textAlign: 'center' }}>{ds.card.noImage}</p>
                            </div>
                            <Badge
                              style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                backgroundColor: getCategoryInfo(product.category)?.color,
                                borderRadius: '25px',
                                padding: '0.5rem 1rem',
                                fontSize: '0.75rem'
                              }}
                            >
                              {getCategoryInfo(product.category)?.name}
                            </Badge>
                          </div>

                          <Card.Body style={{ padding: '1.5rem' }}>
                            <h5 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem', fontSize: '1.1rem', lineHeight: 1.3 }}>
                              {product.name}
                            </h5>

                            <p style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: '1rem' }}>
                              {product.description.length > 120 ? `${product.description.substring(0, 120)}...` : product.description}
                            </p>

                            <div className="mb-3">
                              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0' }}>
                                <div className="text-center">
                                  <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                                    {ds.card.usage}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', lineHeight: '1.3' }}>
                                    {product.keyUse}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mb-3">
                              <h6 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <FaCogs size={12} color="#3b82f6" className="me-2" />
                                {ds.card.tech}
                              </h6>
                              <div style={{ background: '#f1f5f9', borderRadius: '8px', padding: '0.75rem', border: '1px solid #e2e8f0' }}>
                                {product.options.split(',').map((option, idx) => (
                                  <div key={idx} style={{ fontSize: '0.8rem', color: '#475569', marginBottom: idx < product.options.split(',').length - 1 ? '0.3rem' : '0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ width: '4px', height: '4px', backgroundColor: '#3b82f6', borderRadius: '50%', marginRight: '0.5rem', flexShrink: 0 }}></span>
                                    <span style={{ fontWeight: 500 }}>{option.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="d-grid gap-4">
                  {paginatedProducts.map((product, index) => (
                    <motion.div key={product.id} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                      <Card style={{ borderRadius: '20px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <Row className="g-0">
                          <Col md={4}>
                            <div style={{
                              height: '250px',
                              background: `linear-gradient(135deg, ${getCategoryInfo(product.category)?.color}20, ${getCategoryInfo(product.category)?.color}40)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
                            }}>
                              <img src={product.image} alt={product.name} style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
                              <Badge style={{ position: 'absolute', top: '1rem', left: '1rem', backgroundColor: getCategoryInfo(product.category)?.color, borderRadius: '25px', padding: '0.5rem 1rem', fontSize: '0.8rem' }}>
                                {getCategoryInfo(product.category)?.name}
                              </Badge>
                            </div>
                          </Col>
                          <Col md={8}>
                            <Card.Body style={{ padding: '2rem' }}>
                              <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '1rem' }}>{product.name}</h4>
                              <p style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>{product.description}</p>
                              <Row>
                                <Col sm={6}>
                                  <div className="mb-3">
                                    <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ds.card.usage}</small>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{product.keyUse}</div>
                                  </div>
                                </Col>
                                <Col sm={6}>
                                  <div className="mb-3">
                                    <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ds.card.tech}</small>
                                    <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '0.5rem', marginTop: '0.25rem' }}>
                                      {product.options.split(',').map((option, idx) => (
                                        <div key={idx} style={{ fontSize: '0.8rem', color: '#475569', marginBottom: idx < product.options.split(',').length - 1 ? '0.2rem' : '0', display: 'flex', alignItems: 'center' }}>
                                          <span style={{ width: '3px', height: '3px', backgroundColor: '#3b82f6', borderRadius: '50%', marginRight: '0.4rem', flexShrink: 0 }}></span>
                                          <span style={{ fontWeight: 500 }}>{option.trim()}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Col>
                        </Row>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <div className="d-flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button
                        key={i + 1}
                        variant={currentPage === i + 1 ? "primary" : "outline-primary"}
                        onClick={() => setCurrentPage(i + 1)}
                        style={{ borderRadius: '50%', width: '40px', height: '40px', fontWeight: 600 }}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message si aucun résultat */}
              {filteredProducts.length === 0 && (
                <div className="text-center py-5">
                  <FaDesktop size={64} color="#64748b" />
                  <h4 style={{ color: '#64748b', marginTop: '1rem' }}>{ds.card.noResults}</h4>
                  <p style={{ color: '#64748b' }}>{ds.card.noResultsSub}</p>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default MursEcrans;